﻿package pl.zar.tikthrow

import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Bookmark
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.outlined.People
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.lifecycle.viewmodel.compose.viewModel
import pl.zar.tikthrow.shared.domain.User
import pl.zar.tikthrow.ui.screen.AccountScreen
import pl.zar.tikthrow.ui.screen.ApiKeysScreen
import pl.zar.tikthrow.ui.screen.AccountsScreen
import pl.zar.tikthrow.ui.screen.AddPostScreen
import pl.zar.tikthrow.ui.screen.AddQueuedPostScreen
import pl.zar.tikthrow.ui.screen.FeedScreen
import pl.zar.tikthrow.ui.screen.PublishingPlanScreen
import pl.zar.tikthrow.ui.screen.QueueScreen
import pl.zar.tikthrow.ui.screen.SchedulePostScreen
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.AccountsViewModel
import pl.zar.tikthrow.viewmodel.AuthState
import pl.zar.tikthrow.viewmodel.ApiKeysViewModel
import pl.zar.tikthrow.viewmodel.AuthViewModel
import pl.zar.tikthrow.viewmodel.ContentType
import pl.zar.tikthrow.viewmodel.QueueViewModel
import pl.zar.tikthrow.viewmodel.SavedPostsViewModel
import pl.zar.tikthrow.viewmodel.ScheduledPostsViewModel

private enum class AppTab(val label: String, val icon: ImageVector) {
    FEED("Feed", Icons.Default.Home),
    QUEUE("Kolejka", Icons.Default.Bookmark),
    SOCIALS("Socials", Icons.Outlined.People),
    ACCOUNT("Konto", Icons.Default.Person)
}

private sealed interface Screen {
    data object Tabs : Screen
    data object AddPost : Screen
    data class EditPost(val postId: String) : Screen
    data class SchedulePost(val type: ContentType) : Screen
    data class EditScheduledPost(val postId: String, val type: ContentType) : Screen
    data object AddQueuedPost : Screen
    data object EditPlan : Screen
    data object ApiKeys : Screen
}

@Composable
fun App() {
    val authViewModel: AuthViewModel = viewModel { AuthViewModel() }
    val authState by authViewModel.state.collectAsState()
    var currentTab by remember { mutableStateOf(AppTab.FEED) }
    var screen by remember { mutableStateOf<Screen>(Screen.Tabs) }
    val savedPostsViewModel: SavedPostsViewModel = viewModel { SavedPostsViewModel() }
    val scheduledPostsViewModel: ScheduledPostsViewModel = viewModel { ScheduledPostsViewModel() }
    val queueViewModel: QueueViewModel = viewModel { QueueViewModel() }

    MaterialTheme(colorScheme = TikThrowDarkColorScheme) {
        // Desktop windows can be much wider than a phone. List/grid screens (Feed, Kolejka)
        // use the extra room to lay content out in columns; narrow form screens cap their
        // own width internally so they don't stretch into unreadably wide single-line forms.
        Box(
            modifier = Modifier.fillMaxSize().background(Slate50),
            contentAlignment = Alignment.TopCenter
        ) {
            Box(modifier = Modifier.fillMaxHeight().widthIn(max = 1400.dp)) {
                when (val auth = authState) {
                    AuthState.Loading -> LoadingAuthScreen()
                    is AuthState.LoggedOut -> AuthScreen(
                        error = auth.error,
                        onLogin = authViewModel::login,
                        onRegister = authViewModel::register,
                    )
                    is AuthState.LoggedIn -> MainApp(
                        currentTab = currentTab,
                        onTabSelected = { currentTab = it },
                        screen = screen,
                        onScreenChange = { screen = it },
                        savedPostsViewModel = savedPostsViewModel,
                        scheduledPostsViewModel = scheduledPostsViewModel,
                        queueViewModel = queueViewModel,
                        user = auth.user,
                        onLogout = {
                            screen = Screen.Tabs
                            currentTab = AppTab.FEED
                            authViewModel.logout()
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun MainApp(
    currentTab: AppTab,
    onTabSelected: (AppTab) -> Unit,
    screen: Screen,
    onScreenChange: (Screen) -> Unit,
    savedPostsViewModel: SavedPostsViewModel,
    scheduledPostsViewModel: ScheduledPostsViewModel,
    queueViewModel: QueueViewModel,
    user: User,
    onLogout: () -> Unit,
) {
        // Created here (not inside AccountsScreen) so account status loads right after login,
        // regardless of which tab the user lands on first — AccountsScreen/AddQueuedPostScreen
        // resolve to this same instance via viewModel(), so this is the only refresh trigger needed.
        val accountsViewModel: AccountsViewModel = viewModel { AccountsViewModel() }

        when (val s = screen) {
            Screen.AddPost -> AddPostScreen(
                viewModel = savedPostsViewModel,
                onBack = { onScreenChange(Screen.Tabs) }
            )
            is Screen.EditPost -> AddPostScreen(
                viewModel = savedPostsViewModel,
                onBack = { onScreenChange(Screen.Tabs) },
                editPostId = s.postId
            )
            is Screen.SchedulePost -> SchedulePostScreen(
                viewModel = scheduledPostsViewModel,
                type = s.type,
                onBack = { onScreenChange(Screen.Tabs) }
            )
            is Screen.EditScheduledPost -> SchedulePostScreen(
                viewModel = scheduledPostsViewModel,
                type = s.type,
                onBack = { onScreenChange(Screen.Tabs) },
                editPostId = s.postId
            )
            Screen.AddQueuedPost -> AddQueuedPostScreen(
                viewModel = queueViewModel,
                onBack = { onScreenChange(Screen.Tabs) }
            )
            Screen.EditPlan -> PublishingPlanScreen(
                viewModel = queueViewModel,
                onBack = { onScreenChange(Screen.Tabs) }
            )
            // T201: the human entry point of the agent flow (mint / revoke API keys).
            Screen.ApiKeys -> {
                val apiKeysViewModel: ApiKeysViewModel = viewModel { ApiKeysViewModel() }
                ApiKeysScreen(
                    viewModel = apiKeysViewModel,
                    onBack = { onScreenChange(Screen.Tabs) }
                )
            }
            Screen.Tabs -> {
                val tabs = AppTab.entries
                val pagerState = rememberPagerState(
                    initialPage = tabs.indexOf(currentTab),
                    pageCount = { tabs.size }
                )
                val scope = rememberCoroutineScope()
                var swipeActive by remember { mutableStateOf(false) }

                // pager zatrzymał się → aktualizuj zakładkę (nie wyłączaj swipe!)
                LaunchedEffect(pagerState) {
                    snapshotFlow { pagerState.isScrollInProgress }.collect { scrolling ->
                        if (!scrolling) {
                            onTabSelected(tabs[pagerState.currentPage])
                        }
                    }
                }

                // auto-wyłącz po 5s bez swipe'a — restartuj timer po każdej zmianie strony
                LaunchedEffect(swipeActive, pagerState.currentPage) {
                    if (swipeActive) {
                        delay(5_000L)
                        swipeActive = false
                    }
                }

                Scaffold(
                    containerColor = MaterialTheme.colorScheme.background,
                    contentWindowInsets = WindowInsets(0, 0, 0, 0),
                    bottomBar = {
                        FloatingBottomNav(
                            currentTab = currentTab,
                            swipeActive = swipeActive,
                            onTabSelected = { tab ->
                                swipeActive = false
                                onTabSelected(tab)
                                scope.launch { pagerState.animateScrollToPage(tabs.indexOf(tab)) }
                            },
                            onActivateSwipe = { swipeActive = true }
                        )
                    }
                ) { padding ->
                    HorizontalPager(
                        state = pagerState,
                        modifier = Modifier.fillMaxSize(),
                        userScrollEnabled = swipeActive,
                    ) { page ->
                        when (tabs[page]) {
                            AppTab.FEED -> FeedScreen(
                                modifier = Modifier.statusBarsPadding().padding(padding),
                                scheduledViewModel = scheduledPostsViewModel,
                                savedPostsViewModel = savedPostsViewModel,
                                queueViewModel = queueViewModel,
                                onNavigateToEditPlan = { onScreenChange(Screen.EditPlan) },
                                onNavigateToAddQueuedPost = { onScreenChange(Screen.AddQueuedPost) },
                                onNavigateToAddPost = { onScreenChange(Screen.SchedulePost(ContentType.POST)) },
                                onNavigateToAddVideo = { onScreenChange(Screen.SchedulePost(ContentType.VIDEO)) },
                                onNavigateToQueue = { onTabSelected(AppTab.QUEUE) }
                            )
                            AppTab.QUEUE -> QueueScreen(
                                modifier = Modifier.statusBarsPadding().padding(padding),
                                viewModel = savedPostsViewModel,
                                scheduledViewModel = scheduledPostsViewModel,
                                queueViewModel = queueViewModel,
                                onNavigateToAddQueuedPost = { onScreenChange(Screen.AddQueuedPost) },
                                onNavigateToEditPlan = { onScreenChange(Screen.EditPlan) },
                                onNavigateToAccounts = { onTabSelected(AppTab.SOCIALS) },
                                onEditPost = { id -> onScreenChange(Screen.EditPost(id)) },
                                onEditScheduledPost = { id, type -> onScreenChange(Screen.EditScheduledPost(id, type)) }
                            )
                            AppTab.SOCIALS -> AccountsScreen(modifier = Modifier.statusBarsPadding().padding(padding))
                            AppTab.ACCOUNT -> AccountScreen(
                                modifier = Modifier.statusBarsPadding().padding(padding),
                                user = user,
                                onLogout = onLogout,
                                onOpenApiKeys = { onScreenChange(Screen.ApiKeys) },
                            )
                        }
                    }
                }
            }
        }
}

@Composable
private fun LoadingAuthScreen() {
    Box(
        modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background),
        contentAlignment = Alignment.Center
    ) {
        CircularProgressIndicator(color = GradientStart)
    }
}

@Composable
private fun AuthScreen(
    error: String?,
    onLogin: (String, String) -> Unit,
    onRegister: (String, String, String) -> Unit,
) {
    var isRegister by remember { mutableStateOf(false) }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    val canSubmit = email.isNotBlank() && password.length >= 6 && (!isRegister || displayName.isNotBlank())
    val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Box(modifier = Modifier.fillMaxSize().statusBarsPadding(), contentAlignment = Alignment.Center) {
        Column(
            modifier = Modifier.widthIn(max = 440.dp).padding(24.dp),
        ) {
            Text(
                text = "tikthrow",
                fontSize = 34.sp,
                fontWeight = FontWeight.Black,
                style = androidx.compose.ui.text.TextStyle(brush = gradientBrush)
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = if (isRegister) "Utworz konto aplikacji" else "Zaloguj sie do aplikacji",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = Slate900
            )
            Spacer(Modifier.height(22.dp))
            if (isRegister) {
                OutlinedTextField(
                    value = displayName,
                    onValueChange = { displayName = it },
                    label = { Text("Nazwa") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(4.dp)
                )
                Spacer(Modifier.height(10.dp))
            }
            OutlinedTextField(
                value = email,
                onValueChange = { email = it },
                label = { Text("Email") },
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(4.dp)
            )
            Spacer(Modifier.height(10.dp))
            OutlinedTextField(
                value = password,
                onValueChange = { password = it },
                label = { Text("Haslo") },
                visualTransformation = PasswordVisualTransformation(),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(4.dp)
            )
            error?.let {
                Spacer(Modifier.height(10.dp))
                Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            }
            Spacer(Modifier.height(18.dp))
            Surface(
                onClick = {
                    if (canSubmit) {
                        if (isRegister) onRegister(email, password, displayName) else onLogin(email, password)
                    }
                },
                enabled = canSubmit,
                shape = RoundedCornerShape(6.dp),
                color = Color.Transparent,
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(if (canSubmit) gradientBrush else Brush.linearGradient(listOf(Slate200, Slate200)))
                        .padding(vertical = 14.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = if (isRegister) "Zarejestruj" else "Zaloguj",
                        color = if (canSubmit) Color.White else Slate400,
                        fontWeight = FontWeight.ExtraBold
                    )
                }
            }
            TextButton(
                onClick = { isRegister = !isRegister },
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) {
                Text(
                    text = if (isRegister) "Mam juz konto" else "Nie mam konta",
                    color = GradientStart,
                    fontWeight = FontWeight.Bold
                )
            }
        }
        }
    }
}

@Composable
private fun FloatingBottomNav(
    currentTab: AppTab,
    swipeActive: Boolean,
    onTabSelected: (AppTab) -> Unit,
    onActivateSwipe: () -> Unit,
) {
    // Stan buforu: po tapnięciu aktywnej zakładki startuje 1-sekundowy timer
    var bufferActive by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Gdy bufor się aktywuje, po 1s włącz swipe mode
    LaunchedEffect(bufferActive) {
        if (bufferActive) {
            delay(1_000L)
            bufferActive = false
            onActivateSwipe()
        }
    }

    // Reset buforu gdy swipe się dezaktywuje lub tab się zmienia
    LaunchedEffect(currentTab, swipeActive) {
        if (!swipeActive) bufferActive = false
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .navigationBarsPadding()
            .padding(horizontal = 20.dp, vertical = 12.dp),
        contentAlignment = Alignment.Center
    ) {
        Surface(
            shape = RoundedCornerShape(14.dp),
            color = Slate200,
            shadowElevation = 20.dp,
            tonalElevation = 0.dp,
        ) {
            Row(
                modifier = Modifier
                    .padding(6.dp)
                    .animateContentSize(spring(stiffness = Spring.StiffnessMediumLow)),
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppTab.entries.forEach { tab ->
                    val isSelected = tab == currentTab
                    NavPill(
                        tab = tab,
                        selected = isSelected,
                        swipeActive = swipeActive && isSelected,
                        bufferActive = bufferActive && isSelected,
                        onClick = {
                            if (isSelected && !swipeActive) {
                                // Tap na aktywną zakładkę → start buforu
                                bufferActive = true
                            } else if (!isSelected) {
                                // Tap na inną zakładkę → normalna nawigacja
                                bufferActive = false
                                onTabSelected(tab)
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun NavPill(
    tab: AppTab,
    selected: Boolean,
    swipeActive: Boolean,
    bufferActive: Boolean,
    onClick: () -> Unit,
) {
    val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

    // Animacja skali:
    // - swipeActive → pełne powiększenie 1.15x
    // - bufferActive → delikatne pulsowanie 1.07x (sygnał że bufor trwa)
    // - normalnie → 1.0x
    val targetScale = when {
        swipeActive -> 1.15f
        bufferActive -> 1.07f
        else -> 1f
    }
    val pillScale by animateFloatAsState(
        targetValue = targetScale,
        animationSpec = spring(
            dampingRatio = if (bufferActive) Spring.DampingRatioMediumBouncy else Spring.DampingRatioNoBouncy,
            stiffness = Spring.StiffnessMediumLow
        ),
        label = "pillScale"
    )

    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(14.dp),
        color = Color.Transparent,
        modifier = Modifier.scale(pillScale)
    ) {
        Box(
            modifier = Modifier
                .then(
                    if (selected) Modifier
                        .clip(RoundedCornerShape(14.dp))
                        .background(gradientBrush)
                    else Modifier
                )
                .padding(horizontal = if (selected) 18.dp else 14.dp, vertical = 11.dp),
            contentAlignment = Alignment.Center
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(7.dp)
            ) {
                Icon(
                    imageVector = tab.icon,
                    contentDescription = tab.label,
                    modifier = Modifier.size(20.dp),
                    tint = if (selected) Color.White else Slate500
                )
                if (selected) {
                    Text(
                        text = tab.label,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color.White,
                        maxLines = 1
                    )
                }
            }
        }
    }
}
